import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { CouchComponent, CouchVariant } from './couchLibrary';
import { imageRowPatientAxis } from './geometry';
import { Grid } from '~/engine/data/common/Grid';

export interface PlacedContour {
    /** slice normal position in mm along the grid Z axis */
    readonly normalPosition: number;
    /** patient-frame mm, open ring */
    readonly points: ReadonlyArray<ReadonlyVec3>;
}

/**
 * One component's placed geometry. Contours are grouped UNDER their
 * component rather than flattened into one list keyed by `roiName`:
 * nothing in the library forbids two components sharing a name, and a
 * name-keyed join downstream would silently hand both components the
 * union of their geometry — each with its own density override. Grouping
 * makes that state unrepresentable. Aligned 1:1 (by index) with
 * `variant.components`.
 */
export interface PlacedComponent {
    readonly component: CouchComponent;
    readonly contours: readonly PlacedContour[];
}

/**
 * The gate both placement entry points share. See {@link placeCouchModel}
 * for why the refusal exists; failing here means no contour is emitted
 * at all rather than a geometrically wrong one.
 *
 * Deliberately NOT a call into `couchPlacementBlocker`: this is the last
 * line of defence for a caller that skipped the user-facing gate, so it
 * must stay correct with nothing but the grid it is handed. The two agree
 * because both read the same two grid facts — axis-alignment, and whether
 * the image column direction carries a couch depth at all (on a coronal
 * or sagittal volume it runs along the patient instead).
 */
function requirePlaceableGrid(grid: Grid): void {
    if (!grid.isAxisAligned()) {
        throw new Error(
            'placeCouchModel requires an axis-aligned grid; oblique series are not supported'
        );
    }
    if (imageRowPatientAxis(grid.getOrientation()) === 2) {
        throw new Error(
            'placeCouchModel requires transverse slices; the image column direction runs along the patient axis, so it carries no couch depth'
        );
    }
}

type Loop = CouchComponent['loopsXyMm'][number];

/**
 * The loops a component is WRITTEN with: its own outline, and the outline
 * of every component cut out of it as a hole. Filled odd-even — as DICOM
 * receivers and this application's own rasteriser fill a slice's contours
 * — a shell written this way is a ring, and the interior inside it is the
 * only structure covering its hole.
 */
function writtenLoops(
    component: CouchComponent,
    variant: CouchVariant
): readonly Loop[] {
    return [
        ...component.loopsXyMm,
        ...variant.components
            .filter((c) => c.holeIn === component.roiName)
            .flatMap((c) => c.loopsXyMm),
    ];
}

/**
 * Every `holeIn` names a component of the same variant. A hole with no
 * shell to be cut from would leave the two overlapping — the state the
 * field exists to rule out — so it is refused rather than written.
 */
function requireHolesResolve(variant: CouchVariant): void {
    for (const c of variant.components) {
        if (
            c.holeIn !== undefined &&
            !variant.components.some((s) => s.roiName === c.holeIn)
        ) {
            throw new Error(
                `placeCouchModel: ${c.roiName} is cut out of ${c.holeIn}, which variant ${variant.id} does not have`
            );
        }
    }
}

/** A component's loops on one slice — the shared inner step of both
 *  entry points, so the preview's single slice and Apply's full volume
 *  can never disagree on the math. */
function placeLoopsOnSlice(
    loops: readonly Loop[],
    couchTopVoxelY: number,
    grid: Grid,
    sliceIndex: number
): PlacedContour[] {
    const halfSizeX = grid.sizeX / 2;
    const normalPosition = grid.getSliceCenterNormalPosition(sliceIndex);
    return loops.map((loop) => ({
        normalPosition,
        points: loop.map(([x, y]) =>
            grid.toWorldSpace(
                vec3.fromValues(
                    halfSizeX + x / grid.resX,
                    couchTopVoxelY + y / grid.resY,
                    sliceIndex + 0.5
                )
            )
        ),
    }));
}

/** The shared core: both entry points differ only in which slices they
 *  ask for, so the guard, the frame and the maths exist once. */
function placeOnSlices(
    variant: CouchVariant,
    couchTopVoxelY: number,
    grid: Grid,
    sliceIndices: readonly number[]
): readonly PlacedComponent[] {
    requirePlaceableGrid(grid);
    requireHolesResolve(variant);
    const loops = variant.components.map((c) => writtenLoops(c, variant));
    const placed = variant.components.map((component, i) => ({
        component,
        contours: sliceIndices.flatMap((z) =>
            placeLoopsOnSlice(loops[i], couchTopVoxelY, grid, z)
        ),
    }));
    requireCompletePlacement(
        placed,
        loops.reduce((sum, l) => sum + l.length, 0),
        sliceIndices.length
    );
    return placed;
}

/**
 * Postcondition: every written loop of every component is placed on every
 * slice asked for.
 *
 * Holds by construction above, and is asserted anyway because a placement
 * missing a slice is invisible downstream. The writer throws only on a
 * slice it cannot RESOLVE, not on one that was never offered, so a couch
 * silently short of a slice would under-attenuate it in the TPS with
 * nothing to see in the viewport or the export. Counting contours rather
 * than components: the component count is constant however many slices go
 * missing.
 */
function requireCompletePlacement(
    placed: readonly PlacedComponent[],
    loopsPerSlice: number,
    sliceCount: number
): void {
    const expected = sliceCount * loopsPerSlice;
    const actual = placed.reduce((sum, p) => sum + p.contours.length, 0);
    if (actual !== expected) {
        throw new Error(
            `placeCouchModel placed ${actual} contours, expected ${expected} ` +
                `(${sliceCount} slices x ${loopsPerSlice} loops per slice); ` +
                'refusing a couch that does not cover every slice'
        );
    }
}

/**
 * Place a library couch variant into patient space for every slice of
 * `grid`, from the couch-top line's continuous voxel Y.
 *
 * The library frame has x = 0 at the image's lateral centre and y = 0 at
 * the couch top, increasing downward IN THE IMAGE. What makes that frame
 * usable for any patient position is a property of the ACQUISITION, not
 * of patient anatomy: on the scanner the image column direction is the
 * fixed room-down direction toward the table, and
 * ImageOrientationPatient only re-expresses that one room direction in
 * patient coordinates. Which PATIENT axis it lands on, and with which
 * sign, therefore varies — posterior (+Y) supine, anterior (−Y) prone,
 * lateral (±X) decubitus — and that is exactly what must not be
 * hardcoded here. So each loop vertex is resolved entirely in VOXEL
 * space (lateral offset about the image centre, depth below the placed
 * couch top — the same voxel Y the readout and the drawn line use) and
 * only then transformed through
 * `grid.toWorldSpace`, which makes the patient-frame result correct by
 * construction for any axis-aligned orientation, prone and decubitus
 * included, with no per-orientation code.
 *
 * That invariant is narrower than "the image Y axis points room-down",
 * and two things it does NOT cover are unchecked here:
 *
 * - It holds for a series in its ACQUIRED geometry. A volume resampled
 *   onto a neutral axial lattice (the image reformat in
 *   `dicomRewriteImage.ts`) has image +Y = patient +Y by construction,
 *   whatever the patient was lying on, so
 *   image-down is toward the table only if that patient was supine. A
 *   reformatted prone or decubitus series would place the couch on the
 *   wrong side of the patient with every gate below still passing.
 * - It assumes an AXIAL acquisition. A coronally or sagittally acquired
 *   volume is axis-aligned but has its column direction along patient ±Z,
 *   which is not a room-down direction at all;
 *   {@link requirePlaceableGrid} refuses those on that ground.
 *
 * The cross-section does not vary with Z: the same polygon is emitted at
 * every slice rather than interpolated, per the couch library's own
 * documented extrusion. Each emitted `normalPosition` comes from
 * `grid.getSliceCenterNormalPosition`, so every contour lands on an
 * existing slice — the DICOM writer needs that to resolve a SOP UID.
 *
 * Does NOT reposition the couch laterally beyond centring it on the image;
 * an off-centre setup is a downstream drag-correction (SUPPORT ROIs move
 * together via TransformTool), not something this function attempts to
 * predict.
 *
 * Does NOT approximate oblique series. Every emitted contour is placed
 * flat in the slice plane and extruded along the image Z axis — correct
 * only when the slice planes are untilted, because a tilted plane cuts a
 * different shape through a rigid couch than the cross-section stored in
 * the library. Rather than emit a contour that is wrong by an unbounded
 * amount depending on tilt, this throws, so that such a series can be
 * refused ahead of it rather than silently mis-placed.
 *
 * Does NOT clip to the image FOV either: a couch wider or deeper than the
 * scanned volume is placed in full (lateral overhang past a typical
 * 500 mm FOV is the norm for a 530 mm couch), and warning about it is the
 * coverage check's job, not a silent truncation here.
 */
export function placeCouchModel(
    variant: CouchVariant,
    couchTopVoxelY: number,
    grid: Grid
): readonly PlacedComponent[] {
    return placeOnSlices(
        variant,
        couchTopVoxelY,
        grid,
        Array.from({ length: grid.sizeZ }, (_, z) => z)
    );
}

/**
 * {@link placeCouchModel} restricted to ONE slice — the uncommitted
 * viewport preview's entry point, which redraws per mousemove during a
 * drag and only ever shows the current slice. Same core, so the preview
 * and Apply can never disagree on the maths or the grid refusals.
 */
export function placeCouchModelOnSlice(
    variant: CouchVariant,
    couchTopVoxelY: number,
    grid: Grid,
    sliceIndex: number
): readonly PlacedComponent[] {
    return placeOnSlices(variant, couchTopVoxelY, grid, [sliceIndex]);
}
